import {
  aiCopyGroupEditRequestSchema,
  UNCATEGORIZED,
  type AiCopyGroupEditResponse,
  type GroupEditAction,
  type ProductCategoryGroup,
} from '@avrahami/contracts';
import { countText, fill } from '@avrahami/ui';
import { he } from '../i18n/he';
import type { CopyField } from './copy-types';
import { parseWith } from './forms';

/**
 * Editing the copy of a whole group of a store's products (adr/0043): a category with everything
 * below it, a manufacturer, or both. What the form holds, how it is sent, and what came back in
 * words. Nothing here asks the engine to write.
 */

export interface GroupEditDraft {
  /** Empty: any category; `none`: products in no category. */
  categoryId: string;
  /** Empty: any manufacturer. */
  manufacturer: string;
  field: CopyField;
  action: GroupEditAction;
  find: string;
  replace: string;
  text: string;
}

export const EMPTY_GROUP_EDIT: GroupEditDraft = {
  categoryId: '',
  manufacturer: '',
  field: 'description',
  action: 'REPLACE',
  find: '',
  replace: '',
  text: '',
};

/** The request (errors under `group`, `edit.find`, `edit.text`); `preview` only counts. */
export function groupEditRequest(draft: GroupEditDraft, preview: boolean) {
  return parseWith(aiCopyGroupEditRequestSchema, {
    group: {
      ...(draft.categoryId === '' ? {} : { categoryId: draft.categoryId }),
      ...(draft.manufacturer === '' ? {} : { manufacturer: draft.manufacturer }),
    },
    field: draft.field,
    edit:
      draft.action === 'REPLACE'
        ? { action: 'REPLACE', find: draft.find, replace: draft.replace }
        : { action: draft.action, text: draft.text },
    preview,
  });
}

/** The draft names a group and an edit (the save button waits for both). */
export function groupEditReady(draft: GroupEditDraft): boolean {
  const group = draft.categoryId !== '' || draft.manufacturer !== '';
  const edit = draft.action === 'REPLACE' ? draft.find.trim() !== '' : draft.text.trim() !== '';
  return group && edit;
}

export interface CategoryOption {
  id: string;
  /** The name, indented by its depth. */
  label: string;
}

/**
 * The store's categories as choices, each under its parent in the order given; a category whose
 * parent is missing (or in a cycle) is shown at the top. "In no category" comes last.
 */
export function categoryOptions(categories: readonly ProductCategoryGroup[]): CategoryOption[] {
  const ids = new Set(categories.map((category) => category.id));
  const children = new Map<string | null, ProductCategoryGroup[]>();
  for (const category of categories) {
    const parent =
      category.parentId !== null && ids.has(category.parentId) ? category.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), category]);
  }
  const options: CategoryOption[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const category of children.get(parent) ?? []) {
      if (seen.has(category.id)) continue;
      seen.add(category.id);
      options.push({ id: category.id, label: `${'  '.repeat(depth)}${category.name}` });
      walk(category.id, depth + 1);
    }
  };
  walk(null, 0);
  for (const category of categories) {
    if (!seen.has(category.id)) options.push({ id: category.id, label: category.name });
  }
  return [...options, { id: UNCATEGORIZED, label: he.groupEdit.uncategorized }];
}

/** What a group edit did (or, on a preview, would do), in words. */
export function groupEditText(result: AiCopyGroupEditResponse, preview: boolean): string {
  if (result.inGroup === 0) return he.groupEdit.emptyGroup;
  const words = preview ? he.groupEdit.preview : he.groupEdit.done;
  const parts = [
    result.changed === 0
      ? words.none
      : countText(result.changed, { one: words.changedOne, many: words.changedMany }),
  ];
  if (result.skipped > 0) {
    parts.push(fill(he.groupEdit.skipped, { count: result.skipped }));
  }
  if (!preview && result.published > 0) {
    parts.push(fill(he.groupEdit.published, { count: result.published }));
  }
  return `${fill(he.groupEdit.inGroup, { count: result.inGroup })} ${parts.join(' ')}`;
}
